import {
  faCheck,
  faClock,
  faListCheck,
  faPhone,
  faTimer,
  faTrash,
  faTriangleExclamation
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useContext, useEffect, useMemo, useState } from 'react'
import z from 'zod'
import { useNewCallMutation } from '~/api/mutations/action'
import { extractIdsFromIris } from '~/api/utils'
import {
  Dropdown,
  Form,
  InputCol,
  Submit,
  TextareaInput,
  TextInput
} from '~/components/Form'
import { DateInput } from '~/components/Form/Input/DateInput'
import { SubmitAlignment } from '~/components/Form/SubmitAlignment'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import {
  ButtonTabGroup,
  type ButtonTabOption
} from '~/components/Global/Interactions/ButtonTabGroup/ButtonTabGroup'
import { ToastType } from '~/components/Global/Interactions/Toast/ToastType'
import { AsideAction } from '~/components/Scopes/Actions/AsideAction'
import { AsideActionsItems } from '~/components/Scopes/Actions/AsideActionsItems'
import { useCloseAction } from '~/components/Scopes/Actions/hooks/useCloseAction'
import { AddressBookPicker } from '~/domains/actions/components/AddressBookPicker'
import type { AddressBookType } from '~/domains/actions/types'
import { TaskTypeIcon } from '~/domains/tasks/components/TaskTypeIcon/TaskTypeIcon'
import { useAssigneeOptions } from '~/domains/tasks/hooks/useAssigneeOptions'
import { useCreateTaskMutation } from '~/domains/tasks/mutations/createTask'
import { getActionTypeIcon } from '~/domains/tasks/utils/actionTypeIcons'
import { getTaskCreationErrorMessage } from '~/domains/tasks/utils/taskCreationErrorMessage'
import { getTaskWording } from '~/domains/tasks/utils/taskType'
import { useTaxonomy } from '~/domains/taxonomies/queries/useTaxonomy'
import { accessiblePersonTypes } from '~/domains/users/utils/groups'
import { toast } from '~/utils'
import { AsideActionContext, AuthContext } from '~/utils/contextes'
import { optionalDate, requiredDate } from '~/utils/zod'
import { taskLinkedHint, taskTypeOption } from './TaskAction.css'

type CallStatus = 'scheduled' | 'completed' | 'not_completed'

const CALL_TYPE = 'call'
const FREE_TASK_TYPE = 'task'

type TaskStatus = 'scheduled' | 'done'

const taskStatusOptions: ButtonTabOption<TaskStatus>[] = [
  { label: 'Planifiée', value: 'scheduled', icon: faClock },
  { label: 'Terminée', value: 'done', icon: faCheck, variant: 'success' }
]

const statusOptions: ButtonTabOption<CallStatus>[] = [
  { label: 'Planifié', value: 'scheduled', icon: faClock },
  { label: 'Abouti', value: 'completed', icon: faCheck, variant: 'success' },
  {
    label: 'Non abouti',
    value: 'not_completed',
    icon: faTriangleExclamation,
    variant: 'warning'
  }
]

const createFormSchema = (
  selectedType: string,
  selectedStatus: CallStatus | null,
  completionRequired: boolean,
  linkedRequired: boolean
) =>
  z.object({
    datetime:
      selectedType === CALL_TYPE && selectedStatus === 'scheduled'
        ? requiredDate()
        : optionalDate(),
    subject:
      selectedType === FREE_TASK_TYPE
        ? z.string().trim().min(1, 'Veuillez renseigner un sujet')
        : z.string().optional(),
    completionDate: completionRequired ? requiredDate() : optionalDate(),
    notes: z.string().optional(),
    classification: z.string().optional(),
    assignedUser: z.string().min(1, 'Veuillez choisir un utilisateur'),
    linked: linkedRequired
      ? z
          .array(z.string())
          .min(1, 'Sélectionnez au moins un candidat ou un contact')
      : z.array(z.string()).optional()
  })

type FormValues = z.infer<ReturnType<typeof createFormSchema>>

type TaskActionProps = {
  applicantIds?: number[]
  contactIds?: number[]
  orderContext?: {
    id: number
    willChangeStatus: boolean
  }
  defaultType?: 'call' | 'task'
}

export const TaskAction = ({
  applicantIds = [],
  contactIds = [],
  orderContext,
  defaultType = 'call'
}: TaskActionProps) => {
  const callCreationMutation = useNewCallMutation()
  const taskCreationMutation = useCreateTaskMutation()
  const currentUser = useContext(AuthContext)
  // On a CakePHP page there is no auth provider: the legacy config knows the user
  const currentUserId = currentUser?.id ?? window.JobConfig?.user.id
  const defaultAssignee =
    currentUserId !== undefined ? String(currentUserId) : undefined
  const { setIsDirty } = useContext(AsideActionContext)
  const closeAction = useCloseAction()
  const [selectedType, setSelectedType] = useState<string>(defaultType)
  const [selectedStatus, setSelectedStatus] = useState<CallStatus | null>(
    'scheduled'
  )
  const [taskStatus, setTaskStatus] = useState<TaskStatus>('scheduled')
  const hasPersons = applicantIds.length > 0 || contactIds.length > 0
  // Persons picked in "Lié à" when the form starts without any
  const [linkedIris, setLinkedIris] = useState<string[]>([])
  const linkedApplicantCount = hasPersons
    ? applicantIds.length
    : extractIdsFromIris(linkedIris, 'applicants').length
  const linkedContactCount = hasPersons
    ? contactIds.length
    : extractIdsFromIris(linkedIris, 'contacts').length
  // Recruiters link applicants, consultants link contacts; the persons already given win
  const accessibleTypes = accessiblePersonTypes(currentUser)
  const singleAccessibleType =
    accessibleTypes.length === 1 ? accessibleTypes[0] : undefined
  // A custom type belongs to one context: only offer the ones matching the linked persons
  const taxonomyContext =
    linkedContactCount > 0 && linkedApplicantCount === 0
      ? 'contact'
      : linkedApplicantCount > 0 && linkedContactCount === 0
        ? 'applicant'
        : singleAccessibleType
  const isMixed = linkedApplicantCount > 0 && linkedContactCount > 0
  const { data: actionTypes } = useTaxonomy('action_types', {
    context: taxonomyContext
  })
  const assigneeOptions = useAssigneeOptions(currentUser)

  const taskTypes = useMemo(
    () =>
      isMixed
        ? []
        : (actionTypes?.filter((type) => type.expectsCompletion) ?? []),
    [actionTypes, isMixed]
  )
  const classifiableTypes = useMemo(
    () => actionTypes?.filter((type) => !type.expectsCompletion) ?? [],
    [actionTypes]
  )
  const typeOptions = useMemo(
    () => [
      {
        label: 'Appel',
        value: CALL_TYPE,
        element: (
          <span className={taskTypeOption}>
            <TaskTypeIcon icon={faPhone} small />
            Appel
          </span>
        )
      },
      {
        label: 'Tâche libre',
        value: FREE_TASK_TYPE,
        element: (
          <span className={taskTypeOption}>
            <TaskTypeIcon icon={faListCheck} small />
            Tâche libre
          </span>
        )
      },
      ...taskTypes.map((type) => ({
        label: type.name,
        value: String(type.id),
        element: (
          <span className={taskTypeOption}>
            <TaskTypeIcon
              icon={getActionTypeIcon(type.icon) ?? faListCheck}
              color={type.color}
              small
            />
            {type.name}
          </span>
        )
      }))
    ],
    [taskTypes]
  )
  const isCall = selectedType === CALL_TYPE
  const isFreeTask = selectedType === FREE_TASK_TYPE
  const wording = getTaskWording(isCall)
  const completionRequired = isCall
    ? selectedStatus !== 'scheduled'
    : taskStatus === 'done'
  const customType = taskTypes.find((type) => String(type.id) === selectedType)

  // The selected custom type is no longer offered (persons of another context were linked)
  useEffect(() => {
    if (!isCall && !isFreeTask && actionTypes && !customType) {
      setSelectedType(FREE_TASK_TYPE)
    }
  }, [isCall, isFreeTask, actionTypes, customType])
  const linkableTypes: AddressBookType[] = customType?.context
    ? [customType.context]
    : accessibleTypes

  const onSubmit = async (values: FormValues) => {
    if (isCall && selectedStatus === null) {
      return
    }
    const linkedApplicantIds = hasPersons
      ? applicantIds
      : extractIdsFromIris(values.linked ?? [], 'applicants')
    const linkedContactIds = hasPersons
      ? contactIds
      : extractIdsFromIris(values.linked ?? [], 'contacts')
    const assignedUserId = Number.parseInt(values.assignedUser, 10)
    const count = Math.max(
      linkedApplicantIds.length + linkedContactIds.length,
      1
    )
    const onSuccess = () => {
      closeAction(true)
      const plural = count > 1
      toast(
        isCall
          ? plural
            ? `${count} appels ajoutés`
            : 'Appel ajouté'
          : plural
            ? `${count} tâches ajoutées`
            : 'Tâche ajoutée',
        isCall
          ? plural
            ? 'Les appels ont bien été ajoutés'
            : "L'appel a bien été ajouté"
          : plural
            ? 'Les tâches ont bien été ajoutées'
            : 'La tâche a bien été ajoutée',
        ToastType.Success
      )
      if (typeof window.paginatorHistoriqueActions !== 'undefined') {
        window.paginatorHistoriqueActions.refresh()
      }
    }

    if (isCall) {
      const basePayload = {
        applicants: linkedApplicantIds,
        contacts: linkedContactIds,
        notes: values.notes,
        actionType: values.classification || undefined,
        orderId: orderContext?.id,
        assignedUserId
      }
      return callCreationMutation.mutateAsync(
        selectedStatus === 'scheduled' && values.datetime
          ? { ...basePayload, datetime: values.datetime }
          : {
              ...basePayload,
              isCompleted: selectedStatus === 'completed',
              completionDate: values.completionDate
            },
        { onSuccess }
      )
    }

    return taskCreationMutation.mutateAsync(
      {
        subject: isFreeTask ? (values.subject ?? '') : undefined,
        scheduledDate:
          taskStatus === 'scheduled' ? (values.datetime ?? null) : null,
        isCompleted: taskStatus === 'done' ? true : undefined,
        completionDate:
          taskStatus === 'done' ? values.completionDate : undefined,
        notes: values.notes,
        assignedUserId,
        applicantIds: linkedApplicantIds,
        contactIds: linkedContactIds,
        actionTypeId: customType?.id,
        actionTypeName: customType?.name
      },
      {
        onSuccess,
        onError: (error) =>
          toast(
            'Une erreur est survenue',
            getTaskCreationErrorMessage(error),
            ToastType.Danger
          )
      }
    )
  }

  return (
    <Form
      onSubmit={onSubmit}
      schema={createFormSchema(
        selectedType,
        selectedStatus,
        completionRequired,
        // A call needs someone to call; a task can stand alone
        isCall && !hasPersons
      )}
      defaultValues={{
        assignedUser: defaultAssignee
      }}
    >
      <AsideAction
        title="Ajouter une tâche"
        icon={faListCheck}
        applicantIds={applicantIds.length > 0 ? applicantIds : undefined}
        contactIds={
          applicantIds.length === 0 && contactIds.length > 0
            ? contactIds
            : undefined
        }
      >
        <AsideActionsItems>
          <Button
            variation={ButtonVariation.DangerInversed}
            type="button"
            onClick={(e) => {
              e.preventDefault()
              closeAction()
            }}
            buttonSize="small"
          >
            <FontAwesomeIcon icon={faTrash} />
          </Button>
          <Submit
            buttonVariation={ButtonVariation.Primary}
            submitAlignment={SubmitAlignment.Right}
            loadingText="Ajout..."
            successText="Ajouté !"
            onSuccessReset={false}
            buttonSize="small"
          >
            <FontAwesomeIcon icon={faTimer} />
            Ajouter
          </Submit>
        </AsideActionsItems>
        <InputCol>
          <Dropdown
            label="Type"
            name="type"
            required
            options={typeOptions}
            defaultValue={selectedType}
            onChange={(value) => {
              setSelectedType(value)
              setIsDirty(true)
            }}
          />
          {!hasPersons && linkableTypes.length > 0 && (
            <AddressBookPicker
              name="linked"
              label="Lié à"
              placeholder={
                linkableTypes.length === 1 && linkableTypes[0] === 'contact'
                  ? 'Rechercher un contact'
                  : linkableTypes.length === 1
                    ? 'Rechercher un candidat'
                    : 'Rechercher un candidat ou un contact'
              }
              types={linkableTypes}
              onChange={(values) => {
                setLinkedIris(values)
                setIsDirty(true)
              }}
            />
          )}
          {!hasPersons && linkedApplicantCount + linkedContactCount > 1 && (
            <span className={taskLinkedHint}>
              Une tâche sera créée pour chacune des{' '}
              {linkedApplicantCount + linkedContactCount} personnes liées.
            </span>
          )}
          {isFreeTask && (
            <TextInput
              label="Sujet"
              name="subject"
              required
              placeholder="Que faut-il faire ?"
              onChange={() => setIsDirty(true)}
            />
          )}
          {isCall ? (
            <ButtonTabGroup
              options={statusOptions}
              value={selectedStatus}
              onChange={setSelectedStatus}
            />
          ) : (
            <ButtonTabGroup
              options={taskStatusOptions}
              value={taskStatus}
              onChange={(value) => {
                setTaskStatus(value)
                setIsDirty(true)
              }}
            />
          )}
          {(isCall
            ? selectedStatus === 'scheduled'
            : taskStatus === 'scheduled') && (
            <DateInput
              label={`${wording.scheduled} le`}
              name="datetime"
              required={isCall}
              showTime
              noPast
              onChange={() => setIsDirty(true)}
            />
          )}
          {completionRequired && (
            <DateInput
              label={`${wording.treated} le`}
              name="completionDate"
              required
              showTime
              defaultDate={new Date()}
              onChange={() => setIsDirty(true)}
            />
          )}
          {assigneeOptions.length > 0 && (
            <Dropdown
              label={`${wording.assigned} à`}
              name="assignedUser"
              required
              options={assigneeOptions}
              defaultValue={defaultAssignee}
              onChange={() => setIsDirty(true)}
            />
          )}
          {!isCall && (
            <TextareaInput
              label="Notes"
              name="notes"
              onChange={() => setIsDirty(true)}
              placeholder="Ajouter des notes sur la tâche..."
            />
          )}
          {isCall && classifiableTypes.length > 0 && (
            <Dropdown
              label="Notes"
              name="classification"
              placeholder="Classer"
              options={classifiableTypes.map((type) => ({
                label: type.name,
                value: String(type.id)
              }))}
            />
          )}
          {isCall && (
            <TextareaInput
              label={classifiableTypes.length > 0 ? '' : 'Notes'}
              name="notes"
              onChange={() => setIsDirty(true)}
              placeholder="Ajouter des notes sur l'appel..."
            />
          )}
        </InputCol>
      </AsideAction>
    </Form>
  )
}
